import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// Types
export interface Employee {
  id: number;
  name: string;
  position: string;
  department: string;
  status: string;
  salary: number;
  joinDate: string;
  avatar?: string;
}

export interface Patient {
  id: number;
  name: string;
  mrn: string;
  age: number;
  gender: string;
  admissionDate: string;
  department: string;
  status: string;
  room: string;
  doctor: string;
  email?: string;
  phone?: string;
  lastVisit?: string;
  totalVisits?: number;
  loyaltyPoints?: number;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  patient: string;
  amount: number;
  date: string;
  dueDate: string;
  status: string;
  paymentMethod: string;
  insuranceProvider: string;
}

export interface PurchaseOrder {
  id: number;
  supplier: string;
  items: number;
  totalAmount: number;
  orderDate: string;
  expectedDelivery: string;
  status: string;
  priority: string;
}

export interface Asset {
  id: number;
  name: string;
  category: string;
  location: string;
  status: string;
  lastMaintenance: string;
  nextMaintenance: string;
  value: number;
}

export interface Supplier {
  id: number;
  name: string;
  contact: string;
  email: string;
  phone: string;
  category: string;
  rating: number;
  status: string;
  totalOrders: number;
  lastOrder: string;
}

// Store interface
interface AppStore {
  // Data arrays
  employees: Employee[];
  patients: Patient[];
  invoices: Invoice[];
  purchaseOrders: PurchaseOrder[];
  assets: Asset[];
  suppliers: Supplier[];
  
  // UI state
  loading: boolean;
  error: string | null;
  
  // Actions
  addEmployee: (employee: Omit<Employee, 'id'>) => void;
  updateEmployee: (id: number, employee: Partial<Employee>) => void;
  deleteEmployee: (id: number) => void;
  
  addPatient: (patient: Omit<Patient, 'id'>) => void;
  updatePatient: (id: number, patient: Partial<Patient>) => void;
  deletePatient: (id: number) => void;
  
  addInvoice: (invoice: Omit<Invoice, 'id'>) => void;
  updateInvoice: (id: number, invoice: Partial<Invoice>) => void;
  deleteInvoice: (id: number) => void;
  
  addPurchaseOrder: (order: Omit<PurchaseOrder, 'id'>) => void;
  updatePurchaseOrder: (id: number, order: Partial<PurchaseOrder>) => void;
  deletePurchaseOrder: (id: number) => void;
  
  addAsset: (asset: Omit<Asset, 'id'>) => void;
  updateAsset: (id: number, asset: Partial<Asset>) => void;
  deleteAsset: (id: number) => void;
  
  addSupplier: (supplier: Omit<Supplier, 'id'>) => void;
  updateSupplier: (id: number, supplier: Partial<Supplier>) => void;
  deleteSupplier: (id: number) => void;
  
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

// Initial data
const initialEmployees: Employee[] = [
  {
    id: 1,
    name: "Dr. Sarah Al-Rashid",
    position: "Chief Medical Officer",
    department: "Medical",
    status: "Active",
    salary: 25000,
    joinDate: "2020-01-15",
  },
  {
    id: 2,
    name: "Ahmed Hassan",
    position: "HR Manager",
    department: "Human Resources",
    status: "Active",
    salary: 18000,
    joinDate: "2019-03-20",
  },
  {
    id: 3,
    name: "Nurse Fatima Al-Zahra",
    position: "Senior Nurse",
    department: "Nursing",
    status: "Active",
    salary: 12000,
    joinDate: "2021-06-10",
  },
  {
    id: 4,
    name: "Dr. Mohammed Al-Sayed",
    position: "Cardiologist",
    department: "Cardiology",
    status: "On Leave",
    salary: 22000,
    joinDate: "2018-09-05",
  },
];

const initialPatients: Patient[] = [
  {
    id: 1,
    name: "Ahmed Al-Rashid",
    mrn: "MRN-001234",
    age: 45,
    gender: "Male",
    admissionDate: "2024-01-15",
    department: "Cardiology",
    status: "Inpatient",
    room: "C-201",
    doctor: "Dr. Sarah Al-Rashid",
    email: "ahmed@email.com",
    phone: "+966-50-123-4567",
    lastVisit: "2024-01-15",
    totalVisits: 12,
    loyaltyPoints: 1250,
  },
  {
    id: 2,
    name: "Fatima Hassan",
    mrn: "MRN-001235",
    age: 32,
    gender: "Female",
    admissionDate: "2024-01-14",
    department: "Obstetrics",
    status: "Outpatient",
    room: "O-105",
    doctor: "Dr. Mohammed Al-Sayed",
    email: "fatima@email.com",
    phone: "+966-50-987-6543",
    lastVisit: "2024-01-14",
    totalVisits: 8,
    loyaltyPoints: 850,
  },
  {
    id: 3,
    name: "Omar Al-Zahra",
    mrn: "MRN-001236",
    age: 28,
    gender: "Male",
    admissionDate: "2024-01-16",
    department: "Emergency",
    status: "Emergency",
    room: "ER-03",
    doctor: "Dr. Ahmed Hassan",
    email: "omar@email.com",
    phone: "+966-50-555-1234",
    lastVisit: "2023-12-20",
    totalVisits: 3,
    loyaltyPoints: 300,
  },
];

const initialInvoices: Invoice[] = [
  {
    id: 1,
    invoiceNumber: "INV-2024-001",
    patient: "Ahmed Al-Rashid",
    amount: 2500,
    date: "2024-01-15",
    dueDate: "2024-02-15",
    status: "Paid",
    paymentMethod: "Insurance",
    insuranceProvider: "Tawuniya",
  },
  {
    id: 2,
    invoiceNumber: "INV-2024-002",
    patient: "Fatima Hassan",
    amount: 1800,
    date: "2024-01-14",
    dueDate: "2024-02-14",
    status: "Pending",
    paymentMethod: "Cash",
    insuranceProvider: "N/A",
  },
  {
    id: 3,
    invoiceNumber: "INV-2024-003",
    patient: "Omar Al-Zahra",
    amount: 3200,
    date: "2024-01-16",
    dueDate: "2024-02-16",
    status: "Overdue",
    paymentMethod: "Insurance",
    insuranceProvider: "Bupa",
  },
];

const initialPurchaseOrders: PurchaseOrder[] = [
  {
    id: 1,
    supplier: "MedSupply Co.",
    items: 5,
    totalAmount: 2500,
    orderDate: "2024-01-15",
    expectedDelivery: "2024-01-20",
    status: "In Transit",
    priority: "High",
  },
  {
    id: 2,
    supplier: "PharmaTech Ltd.",
    items: 3,
    totalAmount: 1800,
    orderDate: "2024-01-12",
    expectedDelivery: "2024-01-18",
    status: "Delivered",
    priority: "Medium",
  },
  {
    id: 3,
    supplier: "Equipment Solutions",
    items: 2,
    totalAmount: 5000,
    orderDate: "2024-01-10",
    expectedDelivery: "2024-01-25",
    status: "Processing",
    priority: "Low",
  },
];

const initialAssets: Asset[] = [
  {
    id: 1,
    name: "MRI Scanner",
    category: "Medical Equipment",
    location: "Radiology Department",
    status: "Operational",
    lastMaintenance: "2024-01-10",
    nextMaintenance: "2024-04-10",
    value: 2500000,
  },
  {
    id: 2,
    name: "CT Scanner",
    category: "Medical Equipment",
    location: "Radiology Department",
    status: "Maintenance",
    lastMaintenance: "2024-01-15",
    nextMaintenance: "2024-02-15",
    value: 1800000,
  },
  {
    id: 3,
    name: "Operating Room Table",
    category: "Surgical Equipment",
    location: "OR-1",
    status: "Operational",
    lastMaintenance: "2024-01-12",
    nextMaintenance: "2024-03-12",
    value: 150000,
  },
];

const initialSuppliers: Supplier[] = [
  {
    id: 1,
    name: "MedSupply Co.",
    contact: "John Smith",
    email: "john@medsupply.com",
    phone: "+966-50-123-4567",
    category: "Medical Supplies",
    rating: 4.5,
    status: "Active",
    totalOrders: 45,
    lastOrder: "2024-01-10",
  },
  {
    id: 2,
    name: "PharmaTech Ltd.",
    contact: "Sarah Johnson",
    email: "sarah@pharmatech.com",
    phone: "+966-50-987-6543",
    category: "Pharmaceuticals",
    rating: 4.8,
    status: "Active",
    totalOrders: 32,
    lastOrder: "2024-01-12",
  },
  {
    id: 3,
    name: "Equipment Solutions",
    contact: "Mike Wilson",
    email: "mike@equipmentsolutions.com",
    phone: "+966-50-555-1234",
    category: "Medical Equipment",
    rating: 4.2,
    status: "Inactive",
    totalOrders: 18,
    lastOrder: "2023-12-15",
  },
];

// Store implementation
export const useStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Initial state
      employees: initialEmployees,
      patients: initialPatients,
      invoices: initialInvoices,
      purchaseOrders: initialPurchaseOrders,
      assets: initialAssets,
      suppliers: initialSuppliers,
      loading: false,
      error: null,

      // Employee actions
      addEmployee: (employee) => {
        const newEmployee: Employee = {
          ...employee,
          id: Math.max(...get().employees.map(e => e.id), 0) + 1,
        };
        set((state) => ({
          employees: [...state.employees, newEmployee],
        }));
      },

      updateEmployee: (id, employee) => {
        set((state) => ({
          employees: state.employees.map((emp) =>
            emp.id === id ? { ...emp, ...employee } : emp
          ),
        }));
      },

      deleteEmployee: (id) => {
        set((state) => ({
          employees: state.employees.filter((emp) => emp.id !== id),
        }));
      },

      // Patient actions
      addPatient: (patient) => {
        const newPatient: Patient = {
          ...patient,
          id: Math.max(...get().patients.map(p => p.id), 0) + 1,
        };
        set((state) => ({
          patients: [...state.patients, newPatient],
        }));
      },

      updatePatient: (id, patient) => {
        set((state) => ({
          patients: state.patients.map((p) =>
            p.id === id ? { ...p, ...patient } : p
          ),
        }));
      },

      deletePatient: (id) => {
        set((state) => ({
          patients: state.patients.filter((p) => p.id !== id),
        }));
      },

      // Invoice actions
      addInvoice: (invoice) => {
        const newInvoice: Invoice = {
          ...invoice,
          id: Math.max(...get().invoices.map(i => i.id), 0) + 1,
        };
        set((state) => ({
          invoices: [...state.invoices, newInvoice],
        }));
      },

      updateInvoice: (id, invoice) => {
        set((state) => ({
          invoices: state.invoices.map((inv) =>
            inv.id === id ? { ...inv, ...invoice } : inv
          ),
        }));
      },

      deleteInvoice: (id) => {
        set((state) => ({
          invoices: state.invoices.filter((inv) => inv.id !== id),
        }));
      },

      // Purchase Order actions
      addPurchaseOrder: (order) => {
        const newOrder: PurchaseOrder = {
          ...order,
          id: Math.max(...get().purchaseOrders.map(o => o.id), 0) + 1,
        };
        set((state) => ({
          purchaseOrders: [...state.purchaseOrders, newOrder],
        }));
      },

      updatePurchaseOrder: (id, order) => {
        set((state) => ({
          purchaseOrders: state.purchaseOrders.map((o) =>
            o.id === id ? { ...o, ...order } : o
          ),
        }));
      },

      deletePurchaseOrder: (id) => {
        set((state) => ({
          purchaseOrders: state.purchaseOrders.filter((o) => o.id !== id),
        }));
      },

      // Asset actions
      addAsset: (asset) => {
        const newAsset: Asset = {
          ...asset,
          id: Math.max(...get().assets.map(a => a.id), 0) + 1,
        };
        set((state) => ({
          assets: [...state.assets, newAsset],
        }));
      },

      updateAsset: (id, asset) => {
        set((state) => ({
          assets: state.assets.map((a) =>
            a.id === id ? { ...a, ...asset } : a
          ),
        }));
      },

      deleteAsset: (id) => {
        set((state) => ({
          assets: state.assets.filter((a) => a.id !== id),
        }));
      },

      // Supplier actions
      addSupplier: (supplier) => {
        const newSupplier: Supplier = {
          ...supplier,
          id: Math.max(...get().suppliers.map(s => s.id), 0) + 1,
        };
        set((state) => ({
          suppliers: [...state.suppliers, newSupplier],
        }));
      },

      updateSupplier: (id, supplier) => {
        set((state) => ({
          suppliers: state.suppliers.map((s) =>
            s.id === id ? { ...s, ...supplier } : s
          ),
        }));
      },

      deleteSupplier: (id) => {
        set((state) => ({
          suppliers: state.suppliers.filter((s) => s.id !== id),
        }));
      },

      // UI actions
      setLoading: (loading) => set({ loading }),
      setError: (error) => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'elaj-erp-storage',
      partialize: (state) => ({
        employees: state.employees,
        patients: state.patients,
        invoices: state.invoices,
        purchaseOrders: state.purchaseOrders,
        assets: state.assets,
        suppliers: state.suppliers,
      }),
    }
  )
);
